import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO/SEO';

const IndexPage = () => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLogos = async () => {
      try {
        const res = await fetch('/data/logo-list.json?t=' + Date.now());
        const data = res.ok ? await res.json().catch(() => []) : [];
        const items = (Array.isArray(data) ? data : []).map((item, i) => ({
          id: i,
          type: item.logoUrl ? 'image' : 'text',
          src: item.logoUrl || '',
          name: item.name || '',
        }));
        setLogos(items.sort(() => Math.random() - 0.5));
      } catch {
        setLogos([]);
      }
      setLoading(false);
    };
    loadLogos();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="社労士と一緒に労務管理プラットフォーム"
        description="SrWith（エスアールウィズ）は社労士が開発した勤怠＆労務管理プラットフォーム。就業規則・36協定の事前登録で法令違反を予防。中小企業と社労士事務所の「いい会社づくり」を支援します。"
      />
      {/* Hero セクション - フルスクリーン */}
      <div className="w-full min-h-0 md:min-h-screen flex items-start md:items-center justify-center bg-hero-gradient">
        <div className="w-full px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-16 md:pb-12">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-8" style={{color: '#403737'}}>
              社労士と一緒に
            </h1>
            <p className="text-2xl md:text-3xl text-gray-text mb-12">
              SrWith - 労務管理プラットフォーム
            </p>
            <div className="w-full bg-white/80 backdrop-blur-sm p-12 md:p-16 rounded-2xl shadow-2xl">
              <h2 className="text-3xl md:text-4xl font-semibold mb-6" style={{color: '#403737'}}>
                「いい会社」を増やす
              </h2>
              <p className="text-xl md:text-2xl leading-relaxed" style={{color: '#403737'}}>
                法令遵守の適正な労務管理により、従業員が安心して働ける環境づくり
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 企業ロゴ横スクロールセクション - 統合版 */}
      <div className="w-full bg-white py-6">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl sm:text-2xl font-semibold text-center mb-2" style={{color: '#403737'}}>
            信頼できるパートナー事務所
          </h3>
          <p className="text-center text-gray-text mb-6">
            全国の社労士事務所と連携し、質の高いサービスを提供しています
          </p>
          
          {loading ? (
            <div className="text-center">
              <p className="text-gray-text">ロゴを読み込み中...</p>
            </div>
          ) : (
            <div 
              className="overflow-hidden rounded-xl py-6"
              style={{
                background: 'linear-gradient(135deg, #fafafa, #f0f0f0)'
              }}
            >
              <div className="flex animate-scroll">
                {[...logos, ...logos].map((logo, i) => (
                  <div
                    key={`logo-${i}`}
                    className="flex-shrink-0 mx-4 bg-white rounded-lg shadow-sm p-2 w-48 h-20 flex items-center justify-center"
                  >
                    {logo.type === 'image' ? (
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        className="max-w-full max-h-full object-contain min-h-[2rem]"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span
                        className="text-center font-bold leading-tight px-2"
                        style={{
                          fontSize: logo.name.length > 12 ? '11px' : '13px',
                          color: '#403737',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {logo.name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SrWithの特徴セクション - フル幅 */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{color: '#403737'}}>
            SrWithの特徴
          </h2>
          <p className="text-xl md:text-2xl text-gray-text">
            単なる勤怠システムではない、労務プラットフォームとしての価値
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
          {/* 特徴1 */}
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border-t-8 border-pink-beige transform hover:scale-105 transition-transform duration-300">
            <h3 className="text-2xl md:text-3xl font-semibold mb-6" style={{color: '#403737'}}>
              事前登録による予防管理
            </h3>
            <p className="text-lg md:text-xl text-gray-text leading-relaxed">
              就業規則、36協定、変形労働時間制の協定を事前登録し、法令違反の可能性をアラートで予防
            </p>
          </div>

          {/* 特徴2 */}
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border-t-8 border-pink-beige transform hover:scale-105 transition-transform duration-300">
            <h3 className="text-2xl md:text-3xl font-semibold mb-6" style={{color: '#403737'}}>
              社労士の専門知識
            </h3>
            <p className="text-lg md:text-xl text-gray-text leading-relaxed">
              15年以上の実務経験を持つ社労士の専門知識が詰まった労務管理システム
            </p>
          </div>

          {/* 特徴3 */}
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border-t-8 border-pink-beige transform hover:scale-105 transition-transform duration-300">
            <h3 className="text-2xl md:text-3xl font-semibold mb-6" style={{color: '#403737'}}>
              コンサルティング支援
            </h3>
            <p className="text-lg md:text-xl text-gray-text leading-relaxed">
              単なるシステム提供ではなく、社労士と一緒に労務管理を改善していく仕組み
            </p>
          </div>
        </div>
      </div>

      {/* 問い合わせCTAセクション - フル幅 */}
      <div className="w-full bg-pink-beige py-20 md:py-32 pb-58 md:pb-32">
        <div className="w-full px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{color: '#403737'}}>
            まずはお気軽にご相談ください
          </h2>
          <Link to="/contact" className="inline-block bg-accent-red text-white px-12 py-6 rounded-2xl text-xl md:text-2xl font-semibold hover:bg-red-700 transition-all duration-300 transform hover:scale-105 shadow-2xl">
            お問い合わせはこちら
          </Link>
        </div>
      </div>
    </div>
  );
};

export default IndexPage;